import {
  IonButton,
  IonIcon,
  IonInput,
  IonListHeader,
  IonPopover,
} from '@ionic/react';
import { star, starOutline } from 'ionicons/icons';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bookmark,
  useIsBookmarked,
  useToggleBookmark,
  useUpdateBookmark,
} from '../../api/hooks/bookmark.hooks';
import { useIsMounted } from '../../util/hooks';
import { isEnter } from '../../util/keyboard';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { BaseButton } from '../button/base-button';
import { DeleteButton } from '../button/delete-button';
import { IonItem } from '../ionic/IonItem';
import { PopoverState } from '../ui/ui-interfaces';
import './bookmarks.css';

type BookmarkToggleProps = Pick<Bookmark, 'title' | 'type'>;

export const BookmarkToggle = (props: BookmarkToggleProps) => {
  const { type } = props;
  const { i18n } = useTranslation();

  const currentPath = location.pathname;
  const [isBookmarked, existingBookmark] = useIsBookmarked(currentPath);
  const toggleBookmark = useToggleBookmark();
  const updateBookmark = useUpdateBookmark();
  const isMounted = useIsMounted();

  const defaultTitle = existingBookmark?.title ?? props.title;

  const [title, setTitle] = useState<string | null | undefined>(defaultTitle);
  const inputRef = useRef<HTMLIonInputElement>(null);

  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  } as PopoverState);

  const selectInputText = async () => {
    const nativeInput = await inputRef.current?.getInputElement();
    nativeInput?.select();
  };

  const showOptions = (
    e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) => {
    if (popoverState.showPopover === false && isMounted()) {
      e.preventDefault();
      e.stopPropagation();
      e.persist();
      setTitle(defaultTitle);
      setShowPopover({ showPopover: true, event: e as unknown as Event });
    }
  };

  const hideOptions = useCallback(() => {
    if (isMounted()) {
      setShowPopover({ showPopover: false, event: undefined });
    }
  }, [isMounted]);

  const onToggle = useCallback(
    (title: string) => {
      toggleBookmark({ url: currentPath, title, type });
      hideOptions();
    },
    [currentPath, toggleBookmark, type, hideOptions]
  );

  const onUpdate = useCallback(
    (title: string) => {
      updateBookmark({ url: currentPath, title, type });
      hideOptions();
    },
    [updateBookmark, currentPath, type, hideOptions]
  );

  const createButtons = useMemo(() => {
    return (
      <BaseButton
        buttonProps={{
          type: 'submit',
          disabled: !((title?.length ?? 0) > 0),
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (title) {
              onToggle(title);
            }
          },
        }}
      >
        {i18n.t('common:save')}
      </BaseButton>
    );
  }, [onToggle, title, i18n]);

  const editButtons = useMemo(() => {
    return (
      <>
        <DeleteButton
          buttonProps={{
            type: 'submit',
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle('');
            },
          }}
        >
          {i18n.t('common:remove')}
        </DeleteButton>
        <BaseButton
          buttonProps={{
            type: 'submit',
            disabled: !((title?.length ?? 0) > 0),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (title) {
                onUpdate(title);
              }
            },
          }}
        >
          {i18n.t('common:update')}
        </BaseButton>
      </>
    );
  }, [onToggle, onUpdate, title, i18n]);

  return (
    <IonButton onClick={showOptions}>
      <IonIcon icon={isBookmarked ? star : starOutline} />
      <IonPopover
        event={popoverState.event}
        isOpen={popoverState.showPopover}
        onDidPresent={() => {
          selectInputText();
        }}
        onDidDismiss={hideOptions}
      >
        <div className="bookmark-popover-container">
          <IonListHeader>{i18n.t('common:name')}</IonListHeader>
          <IonItem lines="none">
            <IonInput
              ref={inputRef}
              value={title}
              onKeyDown={(e: React.KeyboardEvent<HTMLIonInputElement>) => {
                if (isEnter(e)) {
                  if (title) {
                    onToggle(title);
                  }
                }
              }}
              onIonInput={(e) => setTitle(e.detail.value)}
            />
          </IonItem>
          <RightAlignedButtons>
            {isBookmarked ? editButtons : createButtons}
          </RightAlignedButtons>
        </div>
      </IonPopover>
    </IonButton>
  );
};
